/**
 * `/playlists` — TASK 9.3 Step 1.
 *
 * The mock's `_playlists(V)` view model (L1078) and its template (L439-455),
 * wired to `GET /api/playlists` (TASK 6.2) instead of the in-memory
 * `state.playlists`. What changed and why:
 *
 *   mock `pls.slice(0,n)` + `plsShown` -> ../../components/VirtualList.js over
 *       one cursor PAGE at a time. The endpoint is keyset-paginated at <= 200
 *       and the bench profile is 495 playlists; ui-contract §10.6 pins the
 *       affordance: initial window 40, "Load more playlists ↓", +40.
 *   mock `pls.length+' playlists'` -> the honest held-count label with a "+"
 *       while the server says there is more (a keyset page has no total) —
 *       the same rule ArtistsList.js documents.
 *   mock `_createPlaylist()` -> ./PlaylistModals.js#openCreatePlaylistModal
 *       through the app's OverlayStack, which is what makes the modal
 *       focus-trapped and Escape/back-dismissable (AC 9.1); on success the
 *       FIRST page is re-fetched so the new playlist appears immediately
 *       (AC 8.3) in the server's order rather than a guessed local insert.
 *
 * AC 9.6: skeleton while loading, retriable error card, and an empty state —
 * all rendered into the section BODY so the heading and the create button
 * survive a failed load (§10.6: a failed section never forces a whole-page
 * error).
 *
 * @module
 */

/* global AbortController -- browser platform API; eslint.config.js's browser
   globals block predates the views using it and is outside TASK 9.3's files */

import { ERROR_CODES, TESTIDS, playlistRowLink } from '@streamymusic/shared';

import { VirtualList } from '../../components/VirtualList.js';
import { EmptyBlock, ErrorBlock, LoadingBlock } from '../../components/StateBlocks.js';
import { append, el } from '../../components/primitives/dom.js';
import { gradientFor, initialsOf } from '../../components/primitives/format.js';

import { openCreatePlaylistModal } from './PlaylistModals.js';

/** ui-contract §10.6 scale table — /playlists renders 40, then +40. */
export const PLAYLISTS_PAGE_SIZE = 40;

/** AC 9.6 — the empty-state copy (the mock never seeded an empty list). */
export const NO_PLAYLISTS_HEADLINE = 'NO MIXES YET';
export const NO_PLAYLISTS_MESSAGE =
  'Nothing saved yet — hit “+ NEW PLAYLIST” to start your first mix.';

/**
 * One row of the playlists list (mock L448-452).
 *
 * @param {import('@streamymusic/shared').PlaylistSummary} playlist
 * @returns {HTMLElement}
 */
export function PlaylistRow(playlist) {
  const row = el('a', {
    className: ['sm-row', 'sm-row--shadow', 'sm-row--48'],
    testId: playlistRowLink(playlist.id),
    attrs: { href: `#/playlists/${encodeURIComponent(playlist.id)}` },
  });
  const text = el('span', { className: 'sm-flex-fill' });
  append(
    text,
    el('span', { className: 'sm-line-primary', text: playlist.name }),
    el('span', {
      className: ['sm-line-secondary', 'sm-line-secondary--wrap'],
      text: `${playlist.trackCount.toLocaleString()} track${playlist.trackCount === 1 ? '' : 's'}`,
    }),
  );
  append(
    row,
    el('span', {
      className: ['sm-thumb', 'sm-thumb--44'],
      text: initialsOf(playlist.name),
      styleProps: { background: gradientFor(playlist.id) },
      attrs: { 'aria-hidden': 'true' },
    }),
    text,
  );
  return row;
}

/**
 * Builds the `/playlists` section and starts loading its first page.
 *
 * @param {{
 *   api: Pick<import('../../../api/api-client.js').ApiClient, 'listPlaylists'|'createPlaylist'>,
 *   heading: HTMLElement,
 *   overlays: import('../../app/OverlayStack.js').OverlayStackHandle,
 *   pageSize?: number,
 *   announce?: (text: string) => void,
 *   notify?: (message: string) => void,
 * }} options
 *   `heading` is the route's `<h1 data-testid="page-heading">` (router.js
 *   `PageHeading`) — the shell contract requires exactly one per route.
 *   `overlays` is the shell's stack; the create modal MUST go through it to
 *   inherit the focus trap and Escape/back dismissal. `announce` is the
 *   shell's persistent polite live region; `notify` its toast seam.
 * @returns {{ element: HTMLElement, dispose: () => void }}
 */
export function PlaylistsList(options) {
  const { api, heading, overlays, pageSize = PLAYLISTS_PAGE_SIZE, announce, notify = () => {} } =
    options;

  const section = el('section', { attrs: { 'data-screen-label': 'Playlists' } });

  // mock L440 — heading + spacer + the teal create CTA on one wrapping row.
  const head = el('div', { className: 'sm-page-head' });
  append(
    head,
    heading,
    el('div', { className: 'sm-spacer' }),
    el('button', {
      className: ['sm-btn', 'sm-btn--retry', 'sm-btn--display', 'sm-btn--display-17'],
      text: '+ NEW PLAYLIST',
      testId: TESTIDS.NEW_PLAYLIST_BUTTON,
      attrs: { type: 'button' },
      on: { click: () => openCreateModal() },
    }),
  );

  const countLabel = el('p', { className: 'sm-count-label', attrs: { hidden: true } });
  const body = el('div');
  /** Holds an error for a FAILED "load more" — the loaded rows must survive it. */
  const notice = el('div');
  append(section, head, countLabel, body, notice);

  /** @type {import('@streamymusic/shared').PlaylistSummary[]} */
  let items = [];
  /** @type {string|null} */
  let cursor = null;
  /** Discards an out-of-order settle (a Retry racing the request it retried). */
  let generation = 0;
  let disposed = false;
  /** Cancels the in-flight page fetch on dispose (and on a superseding load). */
  /** @type {AbortController|null} */
  let controller = null;

  const list = VirtualList({
    items,
    windowSize: pageSize,
    renderItem: (playlist) => PlaylistRow(playlist),
    loadMoreLabel: 'Load more playlists ↓',
    loadMoreTestId: TESTIDS.LOAD_MORE_PLAYLISTS_BUTTON,
    listClassName: 'sm-stack-8',
    onLoadMore: () => {
      void load({ append: true });
    },
  });

  function openCreateModal() {
    openCreatePlaylistModal({
      overlays,
      api,
      // The fast client-side duplicate check runs over what is LOADED; the
      // server's 422 covers the rest and lands in the same inline slot.
      existingNames: items.map((playlist) => playlist.name),
      onCreated: (summary) => {
        if (disposed) return;
        notify(`Playlist “${summary.name}” created`);
        // Re-fetch page 1 rather than guessing a local insert position: the
        // list order is the server's, and AC 8.3 only asks that it APPEARS.
        void load();
      },
    });
  }

  /** The honest count line — what we hold, "+" while the server has more. */
  function paintCount() {
    const n = items.length;
    countLabel.textContent = `${n.toLocaleString()}${cursor === null ? '' : '+'} playlist${n === 1 ? '' : 's'}`;
    countLabel.removeAttribute('hidden');
  }

  /**
   * @param {{ append?: boolean }} [opts]
   * @returns {Promise<void>}
   */
  async function load(opts = {}) {
    const appendPage = opts.append === true;
    const own = (generation += 1);
    controller?.abort();
    const ownController = new AbortController();
    controller = ownController;

    notice.replaceChildren();
    if (!appendPage) {
      items = [];
      cursor = null;
      countLabel.setAttribute('hidden', '');
      body.replaceChildren(LoadingBlock({ count: 6, label: 'Loading playlists' }));
    }

    try {
      const page = await api.listPlaylists({
        limit: pageSize,
        ...(appendPage && cursor !== null ? { cursor } : {}),
        signal: ownController.signal,
      });
      if (disposed || own !== generation) return;

      items = appendPage ? [...items, ...page.items] : [...page.items];
      cursor = page.nextCursor;

      if (items.length === 0) {
        body.replaceChildren(
          EmptyBlock({
            headline: NO_PLAYLISTS_HEADLINE,
            message: NO_PLAYLISTS_MESSAGE,
            ...(announce === undefined ? {} : { announce }),
          }),
        );
        countLabel.setAttribute('hidden', '');
        return;
      }

      paintCount();
      list.update({ items, hasMore: cursor !== null });
      if (body.firstChild !== list.element) body.replaceChildren(list.element);
    } catch (error) {
      if (disposed || own !== generation) return;
      const code = /** @type {{ code?: string }} */ (error).code;
      if (appendPage && code === ERROR_CODES.VALIDATION) {
        // A tampered/expired cursor — the documented answer is "start over",
        // not a retry loop against the same bad cursor.
        void load();
        return;
      }
      const retry = ErrorBlock({
        onRetry: () => {
          void load(appendPage ? { append: true } : {});
        },
        ...(announce === undefined ? {} : { announce }),
      });
      if (appendPage) {
        // A failed NEXT page must not discard the pages already on screen.
        list.update({ busy: false });
        notice.replaceChildren(retry);
      } else {
        body.replaceChildren(retry);
      }
    }
  }

  void load();

  return {
    element: section,
    dispose() {
      disposed = true;
      controller?.abort();
      list.dispose();
    },
  };
}
